import { BusMessage, BOOLEAN_BYTES_PER_ELEMENT, MessageFactory, calcStringByteLength } from "flavr-common";
/**
 * Requests that immersive WebXR mode should be enabled or disabled.
 */
export class EnableWebXRMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT; // enable
    }
    constructor(enable) {
        super();
        this.enable = enable;
    }
    serialization(stream) {
        this.enable = stream.boolean(this.enable);
    }
    static is(msg) {
        return msg.type === EnableWebXRMessage.typestatic;
    }
}
EnableWebXRMessage.typestatic = "EnableWebXRMessage";
EnableWebXRMessage.subjectstatic = "general";
MessageFactory.register(EnableWebXRMessage);
/**
 * Reports whether WebXR mode is currently enabled.
 */
export class WebXREnabledMessage extends BusMessage {
    get byteLength() {
        return BOOLEAN_BYTES_PER_ELEMENT; // enabled
    }
    constructor(enabled) {
        super();
        this.enabled = enabled;
    }
    serialization(stream) {
        this.enabled = stream.boolean(this.enabled);
    }
    static is(msg) {
        return msg.type === WebXREnabledMessage.typestatic;
    }
}
WebXREnabledMessage.typestatic = "WebXREnabledMessage";
WebXREnabledMessage.subjectstatic = "general";
MessageFactory.register(WebXREnabledMessage);
/**
 * Reports whether the client is currently presenting in XR together with the latest view transform information.
 */
export class WebXRPresentingMessage extends BusMessage {
    get byteLength() {
        return (BOOLEAN_BYTES_PER_ELEMENT + // isPresenting
            Float32Array.BYTES_PER_ELEMENT * 3 + // position
            Float32Array.BYTES_PER_ELEMENT * 3); // direction
    }
    constructor(isPresenting, position, direction) {
        super();
        this.isPresenting = isPresenting;
        this.position = position;
        this.direction = direction;
    }
    serialization(stream) {
        this.isPresenting = stream.boolean(this.isPresenting);
        this.position = stream.number3Safe(this.position);
        this.direction = stream.number3Safe(this.direction);
    }
    static is(msg) {
        return msg.type === WebXRPresentingMessage.typestatic;
    }
}
WebXRPresentingMessage.typestatic = "WebXRPresentingMessage";
WebXRPresentingMessage.subjectstatic = "general";
MessageFactory.register(WebXRPresentingMessage);
/**
 * Publishes the bounded XR reference space extents for room-scale interactions.
 */
export class BoundedReferenceSpaceMessage extends BusMessage {
    get byteLength() {
        return Float32Array.BYTES_PER_ELEMENT * 3 * 3;
    }
    constructor(areaSize, areaMin, areaMax) {
        super();
        this.areaSize = areaSize;
        this.areaMin = areaMin;
        this.areaMax = areaMax;
    }
    serialization(stream) {
        this.areaSize = stream.number3(this.areaSize);
        this.areaMin = stream.number3(this.areaMin);
        this.areaMax = stream.number3(this.areaMax);
    }
    static is(msg) {
        return msg.type === BoundedReferenceSpaceMessage.typestatic;
    }
}
BoundedReferenceSpaceMessage.typestatic = "BoundedReferenceSpaceMessage";
BoundedReferenceSpaceMessage.subjectstatic = "general";
MessageFactory.register(BoundedReferenceSpaceMessage);
/**
 * Queues a short XR toast notification message for the user.
 */
export class VrToastMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.message) + // message
            Uint32Array.BYTES_PER_ELEMENT + // timeToShowInMs
            Uint32Array.BYTES_PER_ELEMENT + // color
            calcStringByteLength(this.messageID); // messageID
    }
    constructor(message, timeToShowInMs, color, messageID) {
        super();
        this.message = message;
        this.timeToShowInMs = timeToShowInMs;
        this.color = color;
        this.messageID = messageID;
    }
    serialization(stream) {
        this.message = stream.utf16String(this.message);
        this.timeToShowInMs = stream.uint32Safe(this.timeToShowInMs);
        this.color = stream.uint32Safe(this.color);
        this.messageID = stream.utf16StringSafe(this.messageID);
    }
    static is(msg) {
        return msg.type === VrToastMessage.typestatic;
    }
}
VrToastMessage.typestatic = "VrToastMessage";
VrToastMessage.subjectstatic = "general";
MessageFactory.register(VrToastMessage);
/**
 * Queues a message for the XR heads-up display with an optional topic, duration and color.
 */
export class VrHudMessage extends BusMessage {
    get byteLength() {
        return calcStringByteLength(this.message) + // message
            calcStringByteLength(this.topic) +
            Uint32Array.BYTES_PER_ELEMENT + // timeToShowInMs
            Uint32Array.BYTES_PER_ELEMENT + // color
            calcStringByteLength(this.messageID); // messageID
    }
    constructor(message, topic, timeToShowInMs, color, messageID) {
        super();
        this.message = message;
        this.topic = topic;
        this.timeToShowInMs = timeToShowInMs;
        this.color = color;
        this.messageID = messageID;
    }
    serialization(stream) {
        this.message = stream.utf16String(this.message);
        this.topic = stream.utf16String(this.topic);
        this.timeToShowInMs = stream.uint32Safe(this.timeToShowInMs);
        this.color = stream.uint32Safe(this.color);
        this.messageID = stream.utf16StringSafe(this.messageID);
    }
    static is(msg) {
        return msg.type === VrHudMessage.typestatic;
    }
}
VrHudMessage.typestatic = "VrHudMessage";
VrHudMessage.subjectstatic = "general";
MessageFactory.register(VrHudMessage);
